// Route: /voice-tutor/session/[id] — активная Voice Tutor сессия (full-screen).
//
// Роут — ТОНКАЯ обёртка: разбирает URL и отдаёт тело сессии
// (`components/voice-tutor/session-body.tsx`). Само занятие переиспользуется
// сценой воркспейса курса (`?view=voice`) — второй реализации нет, иначе
// handshake, пейсер транскрипта и завершение сессии разъедутся между двумя
// входами. Всё, что здесь остаётся, — полноэкранная геометрия (`layout`) и
// адрес возврата.
//
// Аудио НЕ сохраняется (152-ФЗ / GDPR).

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { VoiceSessionBody } from "@/components/voice-tutor/session-body";
import { VoiceSessionSkeleton } from "@/components/voice-tutor/session-frame";
import { useBackFrom } from "@/hooks/use-back-from";
import { fromSearchSchema } from "@/lib/back-from";

const searchSchema = z
	.object({
		demo: z
			.enum(["connecting", "listening", "speaking", "thinking", "network-drop", "paused"])
			.optional(),
		/** Явный запрос возобновления завершённой сессии (кнопка «Продолжить» в списке). */
		resume: z.boolean().optional(),
	})
	// Адрес возврата: сессию открывают из шага «Добрать» воркспейса курса, и после
	// разбора пробела человек должен вернуться в тему, а не в общий список сессий.
	.merge(fromSearchSchema);

export const Route = createFileRoute("/voice-tutor/session/$id")({
	validateSearch: tolerantSearch(searchSchema),
	component: VoiceTutorSessionPage,
	pendingComponent: () => <VoiceSessionSkeleton layout="overlay" />,
});

/**
 * Выход из сессии — ЕДИНСТВЕННАЯ точка на все кнопки «назад» этого экрана.
 *
 * Голосовая сессия — overlay-роут, и открывают её в том числе из уведомлений и
 * из шага «Добрать» воркспейса курса. Пока выходы были захардкожены на
 * `/voice-tutor`, человек после разбора пробела улетал в общий список сессий
 * вместо своей темы (декрет E29). `?from=` разбирает общий `useBackFrom`;
 * список сессий остаётся фолбэком, когда контекста нет.
 */
function useSessionExit(): () => void {
	const search = useSearch({ from: "/voice-tutor/session/$id" });
	const navigate = useNavigate({ from: "/voice-tutor/session/$id" });
	return useBackFrom(search.from, () => {
		void navigate({ to: "/voice-tutor" });
	});
}

function VoiceTutorSessionPage(): React.ReactElement {
	const { id } = useParams({ from: "/voice-tutor/session/$id" });
	const search = useSearch({ from: "/voice-tutor/session/$id" });
	const exit = useSessionExit();

	return (
		<VoiceSessionBody
			sessionId={id}
			layout="overlay"
			resume={search.resume === true}
			onExit={exit}
		/>
	);
}
